import {
  CRM_CREATED_DATE_PERIODS,
  normalizeCrmCreatedDateValue,
  readCrmCalendarDateDraft,
} from "./crmCreatedDateFilters";

/** Tags §17 deliberately omits the Timeline / Created date Between operator. */
export const CRM_TAG_DATE_CONDITIONS = [
  "is_within",
  "before",
  "after",
] as const;
export type CrmTagDateCondition = (typeof CRM_TAG_DATE_CONDITIONS)[number];
export type CrmTagDateField = "tag_added" | "tag_removed";

export const isCrmTagDateField = (field: string): field is CrmTagDateField =>
  field === "tag_added" || field === "tag_removed";

export const normalizeCrmTagDateValue = (
  condition: string,
  value: unknown,
): string | null =>
  CRM_TAG_DATE_CONDITIONS.some((allowed) => allowed === condition)
    ? normalizeCrmCreatedDateValue(condition, value)
    : null;

export type CrmTagDateConstraint =
  | {
      kind: "instant";
      lower: string;
      upper: string;
      lowerInclusive: boolean;
    }
  | {
      kind: "calendar";
      direction: "before" | "after";
      date: string;
      timeZone: string;
    };

/**
 * A storage-neutral constraint for an EXISTS query over recorded changes, not a
 * latest-event aggregate. Resolve with the list/evaluation's single clock.
 * Calendar comparison happens in the saved zone and excludes the selected day.
 */
export const resolveCrmTagDateConstraint = (
  condition: string,
  value: unknown,
  nowMs: number,
): CrmTagDateConstraint | null => {
  const normalized = normalizeCrmTagDateValue(condition, value);
  if (normalized === null || !Number.isFinite(nowMs)) return null;
  if (condition === "is_within") {
    const period = CRM_CREATED_DATE_PERIODS.find(
      (entry) => entry.value === normalized,
    );
    if (!period) return null;
    const endpoint = nowMs + period.hours * 3_600_000;
    const lower = new Date(Math.min(nowMs, endpoint));
    const upper = new Date(Math.max(nowMs, endpoint));
    if (!Number.isFinite(lower.getTime()) || !Number.isFinite(upper.getTime()))
      return null;
    return {
      kind: "instant",
      lower: lower.toISOString(),
      upper: upper.toISOString(),
      lowerInclusive: period.hours < 0,
    };
  }
  const draft = readCrmCalendarDateDraft(normalized);
  if (!draft || (condition !== "before" && condition !== "after")) return null;
  return {
    kind: "calendar",
    direction: condition,
    date: draft.date,
    timeZone: draft.timeZone,
  };
};
